import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import colors, { fonts, shadows } from '../theme';
import CookieBanner from '../components/CookieBanner';

export default function LandingPage() {
  // eslint-disable-next-line no-unused-vars
    const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    navigate('/pricing');
  };

  return (
    <div style={{
      fontFamily: fonts.landing,
      background: colors.landing.bg,
      minHeight: '100vh',
      color: colors.landing.text,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Onest:wght@300;400;500;600;700;800&display=swap');
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        .animate-in {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }

        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }
        .delay-4 { animation-delay: 0.4s; }

        .stat-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: ${shadows.coralLg};
        }

        .feature-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: ${colors.gradients.coralGlow};
          transition: left 0.5s ease;
        }

        .feature-card:hover::before {
          left: 100%;
        }

        .feature-card:hover {
          transform: translateX(8px);
          box-shadow: -8px 0 0 0 #FF6F61;
        }

        .cta-button {
          background: ${colors.gradients.coral};
          border: none;
          color: ${colors.white};
          padding: 18px 48px;
          font-size: 18px;
          font-weight: 700;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: ${shadows.coralSm};
          position: relative;
          overflow: hidden;
        }

        .cta-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: ${colors.gradients.whiteGlow};
          transition: left 0.5s ease;
        }

        .cta-button:hover::before {
          left: 100%;
        }

        .cta-button:hover {
          transform: translateY(-3px);
          box-shadow: ${shadows.coralMd};
        }

        .cta-button:active {
          transform: translateY(-1px);
        }

        .login-link {
          background: transparent;
          border: 2px solid #FF6F61;
          color: ${colors.landing.coral};
          padding: 10px 28px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .login-link:hover {
          background: ${colors.landing.coral};
          color: ${colors.white};
        }

        .floating-emoji {
          animation: float 6s ease-in-out infinite;
          display: inline-block;
        }

        .gradient-text {
          background: ${colors.gradients.coralExtended};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .step-number {
          font-family: ${fonts.display};
          font-size: 72px;
          opacity: 0.1;
          position: absolute;
          top: -20px;
          left: -10px;
          line-height: 1;
        }
      `}</style>

      {/* Navigation */}
      <nav style={{
        padding: '24px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        background: colors.overlay.landingBg,
        backdropFilter: 'blur(10px)',
        zIndex: 100,
        borderBottom: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <img
            src="/logo192.png"
            alt="Miblio"
            style={{
              width: '40px',
              height: '40px',
            }}
          />
          <span style={{
            fontSize: '32px',
            fontWeight: 800,
            letterSpacing: '-0.02em',
          }} className="gradient-text">
            miblio
          </span>
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button className="login-link" onClick={() => navigate('/login')}>
            Log In
          </button>
          <button className="cta-button" style={{
            padding: '12px 32px',
            fontSize: '16px',
          }} onClick={handleGetStarted}>
            Get Started Free
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        padding: '120px 48px',
        maxWidth: '1400px',
        margin: '0 auto',
        textAlign: 'center',
        position: 'relative',
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: colors.radialGradients.coralLight,
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '8%',
          width: '250px',
          height: '250px',
          background: colors.radialGradients.salmonLight,
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }} />

        <div className="animate-in delay-1" style={{
          fontSize: '24px',
          fontWeight: 600,
          color: colors.landing.darkBg,
          marginBottom: '24px',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>
          Shared Learning for Remote Teams
        </div>

        <h1 className="animate-in delay-2" style={{
          fontSize: '84px',
          fontWeight: 800,
          lineHeight: 1.1,
          marginBottom: '32px',
          letterSpacing: '-0.03em',
          maxWidth: '1100px',
          margin: '0 auto 32px',
        }}>
          <span className="gradient-text">Knowledge Sharing</span><br />
          For Remote Teams
        </h1>

        <p className="animate-in delay-3" style={{
          fontSize: '24px',
          lineHeight: 1.6,
          color: colors.landing.textMuted,
          maxWidth: '800px',
          margin: '0 auto 48px',
          fontWeight: 400,
        }}>
          Turn one person's hard-won lesson into everyone's advantage. Create once, quiz everyone, move faster.
        </p>

        <div className="animate-in delay-4" style={{ marginBottom: '16px' }}>
          <button className="cta-button" onClick={handleGetStarted}>
            Get Started Free
          </button>
        </div>

        <p style={{
          fontSize: '14px',
          color: colors.landing.textLight,
          fontWeight: 500,
        }}>
          No credit card required • Built for remote teams
        </p>
      </section>

      {/* Stats Bar */}
      <section style={{
        background: colors.gradients.coral,
        padding: '60px 48px',
        marginBottom: '120px',
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '48px',
          textAlign: 'center',
        }}>
          {[
            { number: '200%', label: 'Better retention than traditional training' },
            { number: '80%', label: 'Recall accuracy with spaced repetition' },
            { number: '70%', label: 'Average test scores vs 64% for cramming' },
          ].map((stat, i) => (
            <div key={i} style={{ color: colors.white }}>
              <div style={{
                fontSize: '56px',
                fontWeight: 800,
                marginBottom: '8px',
                fontFamily: fonts.display,
              }}>
                {stat.number}
              </div>
              <div style={{
                fontSize: '16px',
                opacity: 0.9,
                fontWeight: 500,
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section style={{
        padding: '120px 48px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <h2 style={{
          fontSize: '56px',
          fontWeight: 800,
          textAlign: 'center',
          marginBottom: '80px',
          letterSpacing: '-0.02em',
        }}>
          How It Works
        </h2>

        <div style={{
          display: 'grid',
          gap: '64px',
        }}>
          {[
            {
              step: '1',
              emoji: '💡',
              title: 'Someone learns something valuable',
              description: 'A teammate figures out the new client workflow, discovers a workaround, or masters a tricky process.',
            },
            {
              step: '2',
              emoji: '✍️',
              title: 'They share it once',
              description: 'Create a quick lesson with the key points. Add a quiz to ensure it sticks. Takes 5 minutes.',
            },
            {
              step: '3',
              emoji: '🚀',
              title: 'Everyone learns it (actually)',
              description: 'Your team gets quizzed at the perfect intervals. No meetings. No repetition. Just retained knowledge.',
            },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '100px 1fr',
              gap: '32px',
              alignItems: 'start',
              position: 'relative',
            }}>
              <div style={{
                fontSize: '72px',
                textAlign: 'center',
              }}>
                <span className="floating-emoji" style={{ animationDelay: `${i * 0.3}s` }}>
                  {item.emoji}
                </span>
              </div>
              <div style={{ position: 'relative' }}>
                <div className="step-number">{item.step}</div>
                <h3 style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  marginBottom: '16px',
                  letterSpacing: '-0.01em',
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontSize: '20px',
                  lineHeight: 1.6,
                  color: colors.landing.textMuted,
                }}>
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section style={{
        padding: '120px 48px',
        background: colors.landing.bgAlt,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
        }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: 800,
            textAlign: 'center',
            marginBottom: '80px',
            letterSpacing: '-0.02em',
          }}>
            Why Teams Love Miblo
          </h2>

          <div style={{
            display: 'grid',
            gap: '32px',
          }}>
            {[
              {
                icon: '🤝',
                title: 'Shared Team Knowledge',
                subtitle: 'Stop losing lessons in Slack threads',
                description: 'Every solved problem becomes team knowledge. When one person learns something valuable, everyone benefits. No more digging through message history or asking "wait, how do we do this again?"\n\nThis isn\'t top-down training—it\'s communal learning. Anyone can create. Everyone can contribute. Your whole team becomes smarter, together.',
              },
              {
                icon: '🧠',
                title: 'Smart Quizzing That Actually Works',
                subtitle: 'Confirmation, not surveillance',
                description: 'Quick quizzes ensure understanding without the micromanagement feel. Team members validate their own knowledge, moving at their own pace.\n\nThe science backs it up: 200% better long-term retention than traditional training, 80% recall accuracy vs. 60% for one-and-done learning, and spaced repetition users score 70% on average vs. 64% for cramming.\n\nThis isn\'t about catching people out—it\'s about making knowledge stick.',
              },
              {
                icon: '⏰',
                title: 'Asynchronous by Design',
                subtitle: 'Learning that fits remote work, not fights it',
                description: 'No more coordinating calendars for training sessions. No live meetings where half the team zones out. Scheduled knowledge drops arrive when your team is ready to learn.\n\nCreate once, schedule it, and move on. Miblo handles the rest while you get back to actual work.',
              },
              {
                icon: '🚀',
                title: 'Onboarding That Doesn\'t Suck',
                subtitle: 'New hires who actually know what they\'re doing',
                description: 'Build your onboarding library once. Every new teammate gets the same essential knowledge, tested for comprehension, without burning hours of your time.\n\nStop repeating the same explanations. Start with a team that\'s actually ready to contribute.',
              },
            ].map((feature, i) => (
              <div key={i} className="feature-card" style={{
                background: colors.landing.bg,
                padding: '48px',
                borderRadius: '16px',
                border: '2px solid #f0f0f0',
              }}>
                <div style={{
                  fontSize: '56px',
                  marginBottom: '24px',
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  marginBottom: '8px',
                  letterSpacing: '-0.01em',
                }}>
                  {feature.title}
                </h3>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: colors.landing.coral,
                  marginBottom: '24px',
                  fontStyle: 'italic',
                }}>
                  {feature.subtitle}
                </div>
                <p style={{
                  fontSize: '18px',
                  lineHeight: 1.7,
                  color: colors.landing.textMuted,
                  whiteSpace: 'pre-line',
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built For Section */}
      <section style={{
        padding: '120px 48px',
        maxWidth: '1000px',
        margin: '0 auto',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontSize: '48px',
          fontWeight: 800,
          marginBottom: '48px',
          letterSpacing: '-0.02em',
        }}>
          Built for Teams of 4-40
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '32px',
          marginBottom: '64px',
        }}>
          {[
            {
              title: 'Small enough to feel personal',
              description: 'No enterprise bloat. Just the features teams actually use.',
            },
            {
              title: 'Big enough to see real impact',
              description: 'When everyone\'s learning together, momentum builds fast.',
            },
            {
              title: 'Everyone creates, everyone learns',
              description: 'Collective knowledge, collective effort. No single point of failure.',
            },
          ].map((item, i) => (
            <div key={i} className="stat-card" style={{
              background: colors.white,
              padding: '32px',
              borderRadius: '12px',
              border: '2px solid #f0f0f0',
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 700,
                marginBottom: '12px',
                color: colors.landing.coral,
              }}>
                {item.title}
              </h3>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.6,
                color: colors.landing.textMuted,
              }}>
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section style={{
        padding: '120px 48px',
        background: colors.gradients.dark,
        textAlign: 'center',
        color: colors.white,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: '400px',
          height: '400px',
          background: colors.radialGradients.coralMedium,
          borderRadius: '50%',
          filter: 'blur(80px)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '15%',
          width: '350px',
          height: '350px',
          background: colors.radialGradients.salmonMedium,
          borderRadius: '50%',
          filter: 'blur(80px)',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: '64px',
            fontWeight: 800,
            marginBottom: '32px',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}>
            Stop teaching the same<br />lesson twice
          </h2>
          
          <p style={{
            fontSize: '24px',
            lineHeight: 1.6,
            maxWidth: '700px',
            margin: '0 auto 48px',
            opacity: 0.9,
          }}>
            Your team's hard-won knowledge shouldn't evaporate. Turn individual discoveries into shared expertise that actually sticks.
          </p>

          <button className="cta-button" style={{
            background: colors.white,
            color: colors.landing.coral,
          }} onClick={handleGetStarted}>
            Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '48px',
        textAlign: 'center',
        background: colors.landing.bg,
        borderTop: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}>
          <img
            src="/logo192.png"
            alt="Miblio"
            style={{
              width: '32px',
              height: '32px',
            }}
          />
          <span style={{
            fontSize: '24px',
            fontWeight: 800,
          }} className="gradient-text">
            miblio
          </span>
        </div>
        <div style={{
          display: 'flex',
          gap: '24px',
          justifyContent: 'center',
          marginBottom: '16px',
        }}>
          <button onClick={() => navigate('/security')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'color 0.2s ease',
          }}
          onMouseOver={(e) => e.currentTarget.style.color = colors.landing.coral}
          onMouseOut={(e) => e.currentTarget.style.color = colors.landing.textLight}
          >
            Security
          </button>
          <button onClick={() => navigate('/privacy')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'color 0.2s ease',
          }}
          onMouseOver={(e) => e.currentTarget.style.color = colors.landing.coral}
          onMouseOut={(e) => e.currentTarget.style.color = colors.landing.textLight}
          >
            Privacy
          </button>
          <button onClick={() => navigate('/terms')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'color 0.2s ease',
          }}
          onMouseOver={(e) => e.currentTarget.style.color = colors.landing.coral}
          onMouseOut={(e) => e.currentTarget.style.color = colors.landing.textLight}
          >
            Terms
          </button>
        </div>
        <p style={{
          fontSize: '14px',
          color: colors.landing.textLight,
        }}>
          © 2024 Miblio. Built for teams who learn together.
        </p>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
}